import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, MapPin, Calendar, Users, CheckCircle2, XCircle, Clock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JoinTrip() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, login, api } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en-US' ? enUS : ptBR;
  
  const [tripInfo, setTripInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('login');
  const [membershipStatus, setMembershipStatus] = useState(null); // null, 'none', 'pending', 'confirmed'
  const [requestSent, setRequestSent] = useState(false);
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    phone: '' 
  });
  const [formLoading, setFormLoading] = useState(false);

  // Fetch trip info by invite code
  useEffect(() => {
    const fetchTripInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/trips/invite/${code}`);
        setTripInfo(response.data);
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        setError(detail || 'Código de convite inválido ou expirado');
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchTripInfo();
    }
  }, [code]);

  // Check membership status when user is logged in
  useEffect(() => {
    const checkMembership = async () => {
      if (!user || !tripInfo || !api) return;
      
      try {
        // Try to get trip details - if user is member, it will work
        const response = await api.get(`/trips/${tripInfo.id}/members`);
        const members = response.data;
        const currentMember = members.find(m => m.user_id === user.id);
        
        if (currentMember) {
          if (currentMember.status === 'confirmed') {
            setMembershipStatus('confirmed');
          } else if (currentMember.status === 'pending') {
            setMembershipStatus('pending');
          } else {
            setMembershipStatus('none');
          }
        } else {
          setMembershipStatus('none');
        }
      } catch (err) {
        // User is not a member or doesn't have access
        setMembershipStatus('none');
      }
    };

    checkMembership();
  }, [user, tripInfo, api]);

  // Redirect if user is confirmed member
  useEffect(() => {
    if (membershipStatus === 'confirmed' && tripInfo) {
      toast.info(t('join.alreadyMember'));
      navigate(`/trip/${tripInfo.id}`, { replace: true });
    }
  }, [membershipStatus, tripInfo, navigate, t]);

  const handleRequestAccess = async () => {
    if (!user || !tripInfo) return;
    
    setRequesting(true);
    try {
      await api.post(`/trips/join/${code}`);
      setRequestSent(true);
      setMembershipStatus('pending');
      toast.success(t('join.requestSuccess'));
    } catch (err) {
      const errorDetail = err.response?.data?.detail || '';
      const lowerDetail = errorDetail.toLowerCase();
      
      if (lowerDetail.includes('pendente') || lowerDetail.includes('aguarde')) {
        setMembershipStatus('pending');
        toast.info(t('join.requestAlreadySent'));
      } else if (lowerDetail.includes('já') || lowerDetail.includes('membro')) {
        setMembershipStatus('confirmed');
        toast.info(t('join.alreadyMember'));
        navigate(`/trip/${tripInfo.id}`, { replace: true });
      } else {
        toast.error(errorDetail || t('errors.somethingWrong'));
      }
    } finally {
      setRequesting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await login(loginForm.email, loginForm.password);
      // After login, the useEffect will check membership
    } catch (err) {
      toast.error(t('auth.loginError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/register`, registerForm);
      toast.success(t('auth.registerSuccess'));
      await login(registerForm.email, registerForm.password);
      // After login, the useEffect will check membership
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.registerError'));
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-sky-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-sky-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              {t('join.invalidInvite')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="rounded-full">
              {t('join.backToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const startDate = tripInfo?.start_date ? 
    (typeof tripInfo.start_date === 'string' ? parseISO(tripInfo.start_date) : tripInfo.start_date) : null;

  // User is logged in - show trip info and request button
  if (user) {
    // Pending request
    if (membershipStatus === 'pending' || requestSent) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-sky-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl">{t('join.requestSent')}</CardTitle>
              <CardDescription>
                {t('join.waitingApproval')}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Trip Info */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                  {tripInfo?.name}
                </h3>
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <MapPin className="w-4 h-4 text-orange-500" />
                  <span>{tripInfo?.destination}</span>
                </div>
                {startDate && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Calendar className="w-4 h-4 text-sky-500" />
                    <span>{format(startDate, "dd 'de' MMMM 'de' yyyy", { locale: dateLocale })}</span>
                  </div>
                )}
              </div>

              <div className="text-center space-y-4">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('join.willBeNotified')}
                </p>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={() => navigate('/dashboard')}
                >
                  {t('tripDetail.goToDashboard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show request access button
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-sky-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✈️</span>
            </div>
            <CardTitle className="text-2xl">{t('join.youWereInvited')}</CardTitle>
            <CardDescription>
              {t('join.requestAccess')}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Trip Info */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                {tripInfo?.name}
              </h3>
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span>{tripInfo?.destination}</span>
              </div>
              {startDate && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <Calendar className="w-4 h-4 text-sky-500" />
                  <span>{format(startDate, "dd 'de' MMMM 'de' yyyy", { locale: dateLocale })}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Users className="w-4 h-4 text-violet-500" />
                <span>{tripInfo?.member_count || 0} {t('trips.members').toLowerCase()}</span>
              </div>
            </div>

            {/* Logged in user info */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
              <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {t('join.connectedAs')} <strong>{user.name}</strong>
              </p>
            </div>

            {/* Request Access Button */}
            <Button 
              className="w-full rounded-full bg-orange-500 hover:bg-orange-600 h-12 text-base"
              onClick={handleRequestAccess}
              disabled={requesting}
            >
              {requesting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <UserPlus className="w-5 h-5 mr-2" />
              )}
              {t('join.requestParticipation')}
            </Button>

            <p className="text-xs text-center text-slate-500 dark:text-slate-400">
              {t('join.organizerWillReceive')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User not logged in - show login/register form
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-sky-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✈️</span>
          </div>
          <CardTitle className="text-2xl">{t('join.youWereInvited')}</CardTitle>
          <CardDescription>
            {t('join.loginToRequest')}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Trip Info */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
            <h3 className="font-bold text-lg text-slate-900 dark:text-white">
              {tripInfo?.name}
            </h3>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <MapPin className="w-4 h-4 text-orange-500" />
              <span>{tripInfo?.destination}</span>
            </div>
            {startDate && (
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Calendar className="w-4 h-4 text-sky-500" />
                <span>{format(startDate, "dd 'de' MMMM 'de' yyyy", { locale: dateLocale })}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <Users className="w-4 h-4 text-violet-500" />
              <span>{tripInfo?.member_count || 0} {t('trips.members').toLowerCase()}</span>
            </div>
          </div>

          {/* Login/Register Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register">{t('auth.register')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="space-y-4 mt-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">{t('auth.email')}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">{t('auth.password')}</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  {t('auth.login')}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register" className="space-y-4 mt-4">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">{t('auth.name')}</Label>
                  <Input
                    id="register-name"
                    type="text"
                    placeholder={language === 'en-US' ? 'Your name' : 'Seu nome'}
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-email">{t('auth.email')}</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-phone">{t('auth.phone')} ({t('common.optional')})</Label>
                  <Input
                    id="register-phone"
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">{t('auth.password')}</Label>
                  <Input
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  {t('auth.register')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          
          <p className="text-xs text-center text-slate-500 dark:text-slate-400">
            {t('join.afterLogin')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
